import React, { useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface ChallengeVoteButtonProps {
  challengeId: string;
  initialScore: number;
  onVoteChange?: (newScore: number) => void;
  compact?: boolean;
}

// Get or create a persistent user identifier for anonymous voting
const getUserIdentifier = (): string => {
  const key = "bwcx_user_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const ChallengeVoteButton: React.FC<ChallengeVoteButtonProps> = ({
  challengeId,
  initialScore,
  onVoteChange,
  compact = false,
}) => {
  const [score, setScore] = useState(initialScore);
  const [userVote, setUserVote] = useState<number>(0);
  const [isVoting, setIsVoting] = useState(false);

  useEffect(() => {
    setScore(initialScore);
  }, [initialScore]);

  useEffect(() => {
    // Fetch user's existing vote
    const fetchUserVote = async () => {
      const userIdentifier = getUserIdentifier();
      const { data } = await supabase
        .from("challenge_votes")
        .select("vote_value")
        .eq("challenge_id", challengeId)
        .eq("user_identifier", userIdentifier)
        .maybeSingle();

      if (data) {
        setUserVote(data.vote_value);
      }
    };
    fetchUserVote();
  }, [challengeId]);

  const handleVote = async (direction: "up" | "down", e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVoting) return;

    setIsVoting(true);
    const userIdentifier = getUserIdentifier();
    const newVoteValue = direction === "up" ? 1 : -1;
    
    // Toggle vote if clicking same direction
    const finalVoteValue = userVote === newVoteValue ? 0 : newVoteValue;
    const scoreDelta = finalVoteValue - userVote;

    // Optimistic update
    const newScore = score + scoreDelta;
    setScore(newScore);
    setUserVote(finalVoteValue);
    onVoteChange?.(newScore);

    try {
      const { error } = await supabase.rpc("update_challenge_vote", {
        p_challenge_id: challengeId,
        p_user_identifier: userIdentifier,
        p_vote_value: finalVoteValue,
      });

      if (error) {
        // Revert on error
        setScore(score);
        setUserVote(userVote);
        onVoteChange?.(score);
        console.error("Vote error:", error);
      }
    } catch (err) {
      // Revert on error
      setScore(score);
      setUserVote(userVote);
      onVoteChange?.(score);
      console.error("Vote error:", err);
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div 
      className={cn(
        "flex flex-col items-center gap-0.5",
        compact ? "min-w-[36px]" : "min-w-[44px]"
      )}
    >
      <button
        onClick={(e) => handleVote("up", e)}
        disabled={isVoting}
        className={cn(
          "p-1 rounded transition-colors hover:bg-primary/10",
          userVote === 1 && "text-primary bg-primary/10",
          isVoting && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Upvote"
      >
        <ChevronUp className={compact ? "h-4 w-4" : "h-5 w-5"} />
      </button>
      
      <span
        className={cn(
          "font-semibold tabular-nums",
          compact ? "text-sm" : "text-base",
          score > 0 && "text-primary",
          score < 0 && "text-destructive",
          score === 0 && "text-muted-foreground"
        )}
      >
        {score}
      </span>
      
      <button
        onClick={(e) => handleVote("down", e)}
        disabled={isVoting}
        className={cn(
          "p-1 rounded transition-colors hover:bg-destructive/10",
          userVote === -1 && "text-destructive bg-destructive/10",
          isVoting && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Downvote"
      >
        <ChevronDown className={compact ? "h-4 w-4" : "h-5 w-5"} />
      </button>
    </div>
  );
};

export default ChallengeVoteButton;
